import React from 'react';
import { computed, observable, action, makeObservable } from 'mobx';

import office from 'src/services/office';

import TargetPicker from 'src/components/base/TargetPicker';
import RangeSelector from 'src/components/Flowsteps/PushData/RangeSelector';
import { NumericFieldWithTooltip } from 'src/components/base/NumericField';
import SearchableComboBox from 'src/components/base/SearchableComboBox';
import CheckboxWithTooltip from 'src/components/base/CheckboxWithTooltip';

import Step from 'src/components/Flowsteps/base/Step';
import Flowstep, { Button, classNames } from 'src/components/Flowsteps/base/Flowstep';

import { FlowstepCategory, RangeType, toDisplayName, toVerbForms } from 'shared/utils/flowstep';
import { getMaxBatchSize, DEFAULT_PARALLEL_COUNT, MAX_PARALLEL_COUNT } from 'shared/excel/runtime/flowstepExecution';


export const Data = {
  DefaultRange: { type: RangeType.Selected },
  Buttons: {
    [Button.Run]: { tooltip: 'Executes the current operation according to the specified settings without saving it inside of the worksheet as a flow step' },
    [Button.RunAndSave]: { tooltip: 'Executes the current operation according to the specified settings and saves all the mappings and settings inside of the flow as a reusable flow step' },
    [Button.Save]: { tooltip: 'Saves all the specified mappings and settings inside of the flow as a reusable flow step' },
    [Button.SaveCopy]: { tooltip: 'Saves all the specified mappings and settings inside of the flow as a separate copy of this flow step' },
  },
};


class PushFlowstep extends Flowstep {
  @observable excelHeaders = [];

  constructor(reactProps, flowstepProps) {
    super(reactProps, flowstepProps);



    this.options = {
      ...this.options,
      rangeToApply: Data.DefaultRange,
      runSynchronously: false,
      batchSize: getMaxBatchSize(this.type),
      parallelCount: DEFAULT_PARALLEL_COUNT,
      showOperationResultsTable: true,
    };

    this.areValid = { ...this.areValid, rangeToApply: true };
    this.types = flowstepProps.types;

    this.typesOptions = this.types.map(x => ({ key: x, text: toDisplayName(x), type: x }));
  }

  @computed get _readOnly_() {
    return !!this.flowstep && !super.license.flowsteps.push.editExisting.can;
  }

  @computed get _buttons_() {
    const { editExisting, createOrRemove } = super.license.flowsteps.push;
    return {
      allowSaveAndDelete: createOrRemove.unlimited.can,
      allowSaveCopy: true,
      allowEditExisting: editExisting.can,
      data: Data.Buttons,
    };
  }

  async _componentDidMountStart_({ range, type, id }) {
    this.type = type ?? this.type;
    if (!id) {
      this.options.rangeToApply = range ? { type: range } : Data.DefaultRange;
    }
  }

  async _loadFlowstepOnMount_(flowstep) {
    this.setSelectionTarget(await office.getFlowstepTarget({ worksheetId: flowstep.worksheetId, targetCell: flowstep.target }));
    await this._loadFlowstep_(flowstep, true);
  }

  _setSelectionTarget_(selectionTarget) {
    this.excelHeaders = selectionTarget.table?.header.values ?? [];
  }

  loadFlowstep(flowstep, isExact) {
    super.loadFlowstep(flowstep, isExact);

    const {
      parallelCount, runSynchronously, batchSize, rangeToApply,
      showOperationResultsTable
    } = flowstep.options;

    this.options = {
      ...this.options,
      batchSize,
      runSynchronously: runSynchronously ?? false,
      parallelCount,
      rangeToApply,
      showOperationResultsTable: showOperationResultsTable ?? true
    };
  }

  makeFlowstep(makeCopy) {
    const flowstep = super.makeFlowstep(makeCopy);

    const {
      parallelCount, runSynchronously, batchSize, rangeToApply, showOperationResultsTable
    } = this.options;
    const { worksheetId, targetCell: target } = this.selectionTarget;

    Object.assign(flowstep, {
      category: FlowstepCategory.Push,
      target, worksheetId,
    });
    Object.assign(flowstep.options, {
      parallelCount, runSynchronously, batchSize, rangeToApply, showOperationResultsTable,
    });

    return flowstep;
  }


  _renderBody_(renderProps) {
    const { getStepTitle } = renderProps;
    const { inf } = toVerbForms(this.type);

    return (
      <div className={classNames.content}>

        {this.types.length > 1 &&
          <Step title={getStepTitle('Select action')}>
            <SearchableComboBox
              options={this.typesOptions}
              selectedKey={this.type}
              disabled={this._readOnly_}
              onChange={(_, item) => this.handleTypeChange(item.type)}
            />

            {this._renderTypeAction_?.(renderProps)}
          </Step>
        }

        <Step title={getStepTitle('Verify range')}>
          <TargetPicker target={this.selectionTarget} disabled={this._readOnly_} onChange={this.handleTargetChange} />
        </Step>

        {this._renderCustomSteps_?.(renderProps)}

        <Step title={getStepTitle(`Choose data rows to ${inf}`)}>
          <RangeSelector
            rangeToApply={this.options.rangeToApply}
            flowstepType={this.type}
            headers={this.excelHeaders}
            disabled={this._readOnly_}
            onChange={(value, isValid) => this.handleOptionsAndIsValidChange('rangeToApply', value, isValid)}
          />
        </Step>

        {/* { this.renderErrorReaction(renderProps, { name: 'flow step', action: 'executed' }) } */}

      </div>
    );
  }


  _renderCustomOptions_() {
    return (
      <>
        <CheckboxWithTooltip
          label="Show operation results"
          tooltip={'Enable, if you would like to see a table alongside your table with data, where results of executed operations are displayed'}
          disabled={this._readOnly_}
          checked={this.options.showOperationResultsTable}
          onChange={(event, checked) => this.handleOptionsChange('showOperationResultsTable', checked)}
        />

        <CheckboxWithTooltip
          label="Sequential batch processing"
          tooltip={`By default, we try to upload several batches of data at the same time. If this is causing issues with your Salesforce implementation and you'd like to upload all batches sequentially - check this box`}
          disabled={this._readOnly_}
          checked={this.options.runSynchronously}
          onChange={(event, checked) => this.handleOptionsChange('runSynchronously', checked)}
        />

        <NumericFieldWithTooltip
          label={'Batch size'}
          tooltip={'The data will be split into batches of this size before uploading to Salesforce. Decrease this value if you are experiencing issues with your triggers or processes during the upload.'}
          min={1}
          max={getMaxBatchSize(this.type)}
          disabled={this._readOnly_}
          value={this.options.batchSize}
          onChange={(value) => this.handleOptionsChange('batchSize', value)}
        />

        {false &&
          <NumericFieldWithTooltip
            label={'Parallel count'}
            min={1}
            max={MAX_PARALLEL_COUNT}
            disabled={this._readOnly_}
            value={this.options.parallelCount}
            onChange={(value) => this.handleOptionsChange('parallelCount', value)}
          />
        }
      </>
    );
  }

  @action handleTypeChange = (type) => {
    this.type = type;
    this._setType_?.(type);
    this.setDirty();
    this.props.pageControl.setTitle(this.pageTitle);
  }

  handleTargetChange = async (target) => {
    this.setSelectionTarget(target);
    if (!this.flowstep) {
      await this.findAndLoadSimilarFlowstep(false);
    }
    this.setDirty();
  }
}


export default PushFlowstep;
